import React, { Dispatch, SetStateAction } from 'react';
import {
  Avatar,
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { ArrowBackIosRounded } from '@mui/icons-material';
import { GeneralRequests } from '@prisma/client';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { QueryKey } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getFullName } from '../../helpers/global';
import { getInitials, important } from '../../utils/global/helper';
import { ChatFilter, UserConversation } from '../../types/chats';
import DropdownMenu from '../global/DropdownMenu';
import useUser from '../../hooks/global/useUser';
import useNavigateToCustomerProfile from '../../hooks/customers/useNavigateToCustomerProfile';
import { FooterAction, LoadedUser } from '../../interfaces';
import { ROUTES } from '../../constants/global';
import useMuteChat from '../../hooks/chat/useMuteChat';
import {
  getArchivedKey,
  getChatParticipants,
  getImportantKey,
  getParticipantDisplayName,
  getParticipantId,
  isConversationArchived,
  isConversationImportant,
  updateConversationInList,
} from '../../helpers/chats';
import { getEmployeesToAdd } from '../../helpers/chats/getEmployeesToAdd';
import { getAppointmentStatus } from '../../helpers/appointments';
import useGetChatHeaderAppointment from '../../hooks/chat/useGetChatHeaderAppointment';
import useArchiveChat from '../../hooks/chat/useArchiveChat';
import useMarkChatImportant from '../../hooks/chat/useImportantChat';
import { getConversation, resetConversations } from '../../services/chats';
import useLeaveConversation from '../../hooks/chat/useLeaveConversation';
import useGetInternalConversation from '../../hooks/chat/useGetInternalConversation';
import useAwaitedForm from '../../hooks/useAwaitedForm';
import useConfirm from '../../hooks/useConfirm';
import useAddParticipant from '../../hooks/chat/useAddParticipant';

interface Props {
  conversation: UserConversation;
  onBack: () => void;
  queryKey: QueryKey;
  updateConversation: Dispatch<SetStateAction<UserConversation | null>>;
}

const DEFAULT_HEADER = {
  status: '-',
  isRequest: false,
};

function ChatBoxHeader({
  conversation,
  onBack,
  queryKey,
  updateConversation,
}: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const navigateToCustomerProfile = useNavigateToCustomerProfile();
  const { user } = useUser<LoadedUser>();
  const asyncForm = useAwaitedForm();
  const confirm = useConfirm();

  const { appointment, appointmentId, isLoading } =
    useGetChatHeaderAppointment(conversation);
  const { data: conversationData, refetch: refetchConversation } =
    useGetInternalConversation(conversation.id);
  const appointmentImage = appointment?.refImgsId?.[0];

  const { mutate: muteChat, isPending: isMutingChat } = useMuteChat({
    onSuccess: muted => {
      const updatedParticipants = conversation.participants.map(p =>
        p.participantId === user.userId ? { ...p, notify: !muted } : p,
      );
      updateConversationInList(queryKey, {
        ...conversation,
        participants: updatedParticipants,
      });
      toast.success(`Chat ${muted ? 'muted' : 'unmuted'} successfully.`);
    },
    onError: mute => {
      toast.error(`Failed to ${mute ? 'mute' : 'unmute'} chat.`);
    },
  });
  const { mutate: archiveChat, isPending: isArchivingChat } = useArchiveChat({
    onSuccess: archived => {
      toast.success(
        `Chat ${archived ? 'archived' : 'unarchived'} successfully.`,
      );
      const archivedKey = getArchivedKey(user.userId);
      const updatedCustom = {
        ...(conversation.custom ?? {}),
        [archivedKey]: String(archived),
      };
      // since the conversation will no longer be in the current list, we need to update the conversation state
      updateConversation(prev =>
        prev
          ? {
              ...prev,
              custom: updatedCustom,
            }
          : null,
      );
      // choose reset over invalidate to avoid refetching ALL pages the user has scrolled through
      resetConversations();
    },
  });
  const { mutate: toggleImportant, isPending: isMarkingImportant } =
    useMarkChatImportant({
      onSuccess: isImportant => {
        toast.success(
          `Chat ${
            isImportant ? 'marked as important' : 'marked not important'
          }.`,
        );
        const importantKey = getImportantKey(user.userId);
        const updatedCustom = {
          ...(conversation.custom ?? {}),
          [importantKey]: String(isImportant),
        };
        updateConversation(prev =>
          prev
            ? {
                ...prev,
                custom: updatedCustom,
              }
            : null,
        );
        updateConversationInList(queryKey, {
          ...conversation,
          custom: updatedCustom,
        });

        resetConversations(ChatFilter.IMPORTANT);
      },
    });

  const { mutate: leaveConversation, isPending: isLeavingConversation } =
    useLeaveConversation({
      onSuccess: () => {
        resetConversations();
        onBack();
        toast.success('You have left the chat.');
      },
    });

  const { mutate: addParticipant, isPending: isAddingParticipant } =
    useAddParticipant({
      onSuccess: async () => {
        refetchConversation();

        const updatedConversation = await getConversation(
          conversation.id,
        ).catch(() => null);
        if (!updatedConversation) return;

        updateConversationInList(queryKey, updatedConversation);
        updateConversation(prev =>
          prev
            ? {
                ...prev,
                participants: updatedConversation.participants,
              }
            : null,
        );

        toast.success('Added to chat!');
      },
    });

  const isPending =
    isMutingChat ||
    isArchivingChat ||
    isMarkingImportant ||
    isLeavingConversation ||
    isAddingParticipant;

  const isArchived = isConversationArchived({
    conversation,
    userId: user.userId,
  });
  const isImportant = isConversationImportant({
    conversation,
    userId: user.userId,
  });
  const participation = conversation.participants.find(
    p => p.participantId === user.userId,
  );

  const {
    customerParticipant,
    otherParticipants: filteredParticipants,
    initials,
  } = getChatParticipants(conversation, user);

  const customerInitials = appointment?.customer
    ? getInitials(appointment.customer)
    : initials;
  const uniqueParticipantIds = new Set(
    conversation.participants.map(p => p.userId),
  );
  const uniqueParticipants = Array.from(uniqueParticipantIds).map(
    id => conversation.participants.find(p => p.userId === id)!,
  );
  const participantsString = (
    filteredParticipants.length > 0 ? filteredParticipants : uniqueParticipants
  )
    .map(p =>
      getFullName({
        firstName: p.firstName || null,
        lastName: p.lastName ? p.lastName[0] : '',
      }),
    )
    .join(', ');

  const customerName = `${appointment?.customer?.firstName || ''}
          ${
            appointment?.customer?.lastName
              ? `${appointment?.customer?.lastName[0]}.`
              : ''
          }`;

  const { status, isRequest } = React.useMemo(() => {
    if (!appointment) return DEFAULT_HEADER;
    const apptStatus = getAppointmentStatus({
      status: appointment.status,
      previousStatus: appointment.previousStatus,
    });
    if (apptStatus === 'REQUESTED') {
      const requestedOn = new Date(appointment.createdAt || '');
      const requestedText = format(requestedOn, 'MMM d, yyyy');
      return {
        status: `Requested on ${requestedText}`,
        isRequest: true,
      };
    }
    if (apptStatus === 'ACCEPTED') {
      const acceptedOn = new Date(
        appointment.acceptedOn || appointment.updatedAt || '',
      );
      const acceptedText = format(acceptedOn, 'MMM d, yyyy');
      return {
        status: `Accepted on ${acceptedText}`,
        isRequest: true,
      };
    }
    if (apptStatus === 'REJECTED') {
      const acceptedOn = new Date(
        appointment.rejectedOn || appointment.updatedAt || '',
      );
      const acceptedText = format(acceptedOn, 'MMM d, yyyy');
      return {
        status: `Rejected on ${acceptedText}`,
        isRequest: true,
      };
    }
    if (appointment.checkoutSessionCompleted) {
      const completedOn = new Date(appointment.startDateTime || '');
      const completedText = format(completedOn, 'MMM d, yyyy');
      return {
        status: `Completed on ${completedText}`,
        isRequest: false,
      };
    }
    if (apptStatus === 'CONFIRMED') {
      const scheduledOn = new Date(appointment.startDateTime || '');
      const scheduledText = format(scheduledOn, 'MMM d, yyyy h:mma');
      return {
        status: `Confirmed for ${scheduledText}`,
        isRequest: false,
      };
    }
    if (apptStatus === 'SCHEDULED') {
      const scheduledOn = new Date(appointment.startDateTime || '');
      const scheduledText = format(scheduledOn, 'MMM d, yyyy h:mma');
      return {
        status: `Scheduled for ${scheduledText}`,
        isRequest: false,
      };
    }
    if (apptStatus === 'CANCELED') {
      const acceptedOn = new Date(appointment.updatedAt || '');
      const acceptedText = format(acceptedOn, 'MMM d, yyyy');
      return {
        status: `Canceled on ${acceptedText}`,
        isRequest: false,
      };
    }
    if (apptStatus === 'ARCHIVED') {
      const archivedOn = new Date(
        appointment.archivedAt || appointment.updatedAt || '',
      );
      const archivedText = format(archivedOn, 'MMM d, yyyy');
      return {
        status: `Archived on ${archivedText}`,
        isRequest: false,
      };
    }
    if ((appointment as unknown as GeneralRequests).requestId) {
      const requestedOn = new Date(appointment.createdAt || '');
      const requestedText = format(requestedOn, 'MMM d, yyyy');
      return {
        status: `Requested on ${requestedText}`,
        isRequest: true,
      };
    }
    return DEFAULT_HEADER;
  }, [appointment]);

  const { header, subHeader } = React.useMemo(
    () => ({
      header: getParticipantDisplayName(customerParticipant, true),
      subHeader: `Chat with: ${participantsString}`,
    }),
    [customerParticipant, participantsString],
  );

  const url = appointment
    ? `${ROUTES[isRequest ? 'requests' : 'calendar']}/${
        'requestId' in appointment && appointment.requestId
          ? appointment.requestId
          : appointment?.appointmentId
      }`
    : '';

  const employeesToAdd = React.useMemo(
    () =>
      getEmployeesToAdd({
        conversationData,
        employees: user.business?.employees,
        services: user.business?.services,
        currentUserId: user.userId,
        currentBusinessId: user.business.id,
        participants: conversation.participants,
      }),
    [
      conversationData,
      user.business.id,
      user.business?.employees,
      user.business?.services,
      user.userId,
      conversation.participants,
    ],
  );

  const addEmployeeToChat = React.useCallback(async () => {
    if (!employeesToAdd.length) return;

    const employeeOptions = employeesToAdd.map(e => ({
      label:
        e.displayName ||
        getFullName({
          firstName: e.user?.firstName,
          lastName: e.user?.lastName,
        }),
      value: e.employeeId,
    }));
    const response = await asyncForm({
      form: {
        title: 'Add team member to chat',
        submitButtonText: 'Add team member to chat',
        initialValues: {
          employeeId: '',
        },
        inputRows: [
          [
            {
              inputType: 'select',
              name: 'employeeId',
              label: 'Team member',
              options: employeeOptions,
              placeholder: 'Select team member',
              autoWidth: false,
            },
          ],
        ],
      },
    });
    if (!response?.employeeId) return;

    const selectedEmployee = employeeOptions.find(
      e => e.value === response.employeeId,
    );
    if (!selectedEmployee) return;

    const confirmed = await confirm({
      title: 'Add team member to chat',
      message: `Adding ${selectedEmployee.label} to the chat won't give them access to the client's request. If you'd like to add them to the request, please go to the Requests page and add them from there.`,
      dismissible: false,
      hideCloseButton: true,
      negativeText: 'Cancel',
      positiveText: 'Add to chat',
    });
    if (!confirmed) return;

    addParticipant({
      conversationId: conversation.id,
      userId: selectedEmployee.value,
    });
  }, [asyncForm, confirm, employeesToAdd, addParticipant, conversation.id]);

  const actions = React.useMemo<FooterAction[]>(() => {
    if (!appointment && appointmentId) return [];
    const customerId =
      appointment?.customerId || getParticipantId(customerParticipant?.userId);
    const chatHasOtherEmployees = user?.business?.employees.some(
      e =>
        e.employeeId !== user.userId &&
        // check participantId, not userId
        conversation.participants.some(p => p.participantId === e.employeeId),
    );
    const canLeave =
      !!chatHasOtherEmployees &&
      !!conversationData &&
      !!conversationData.businessId &&
      (!conversationData.employeeId ||
        conversationData.employeeId !== user.userId);

    const tempActions = [
      {
        label: 'View Customer',
        action: () => navigateToCustomerProfile(customerId),
      },
      {
        label: isImportant ? 'Mark not important' : 'Mark as important',
        action: () =>
          toggleImportant({
            conversationId: conversation.id,
            important: !isImportant,
          }),
      },
      {
        label: participation?.notify ? 'Mute Chat' : 'Unmute Chat',
        action: () =>
          muteChat({
            conversationId: conversation.id,
            muted: !!participation?.notify,
          }),
      },
      employeesToAdd.length > 0 && {
        label: 'Add team member to chat',
        action: addEmployeeToChat,
      },
      {
        label: isArchived ? 'Unarchive Chat' : 'Archive Chat',
        action: () =>
          archiveChat({
            conversationId: conversation.id,
            archived: !isArchived,
          }),
      },
      canLeave && {
        label: 'Leave Chat',
        action: () => leaveConversation(conversation.id),
      },
    ].filter(Boolean) as FooterAction[];

    if (appointment) {
      tempActions.unshift({
        label: 'View Request',
        action: () => navigate(url),
      });
    }

    return tempActions;
  }, [
    appointment,
    appointmentId,
    customerParticipant?.userId,
    user?.business?.employees,
    user.userId,
    conversationData,
    participation?.notify,
    employeesToAdd.length,
    addEmployeeToChat,
    isArchived,
    isImportant,
    conversation.participants,
    conversation.id,
    navigate,
    navigateToCustomerProfile,
    muteChat,
    archiveChat,
    toggleImportant,
    leaveConversation,
    url,
  ]);

  return (
    <Box
      p={3}
      zIndex={1}
      boxShadow="0px 4px 13px 0px #0000001A;"
      display="flex"
      alignItems="center"
      gap={1}>
      <Button
        onClick={onBack}
        sx={{
          minWidth: 0,
          height: '100%',
          p: 0,
          px: 1,
          display: {
            xs: 'block',
            md: 'none',
          },
        }}>
        <ArrowBackIosRounded
          htmlColor={theme.palette.darkGrey.main}
          width={20}
        />
      </Button>

      {!isLoading ? (
        <Avatar
          src={appointmentImage || undefined}
          alt={customerInitials}
          sx={{
            bgcolor: theme.palette.darkerLightGrey.main,
            width: 40,
            height: 40,
          }}>
          {!appointmentImage && (
            <Typography color="text.secondary" variant="h5">
              {customerInitials}
            </Typography>
          )}
        </Avatar>
      ) : (
        <Skeleton variant="circular" width={40} height={40} />
      )}

      <Stack gap={0.5} flex={1}>
        {!!appointment && !isLoading && (
          <>
            <Typography variant="h5" component="p" color="text.primary">
              {isRequest ? 'Request' : 'Appointment'} with {customerName}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {status}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {subHeader}
              {isArchived && (
                <Typography
                  component="span"
                  variant="inherit"
                  color="text.secondary">
                  {' '}
                  (Archived)
                </Typography>
              )}
            </Typography>
          </>
        )}
        {isLoading && (
          <>
            <Skeleton variant="text" width={120} height={17.5} />
            <Skeleton variant="text" width={100} height={17} />
            <Skeleton variant="text" width={100} height={17} />
          </>
        )}
        {!appointment && !isLoading && (
          <>
            <Typography variant="h5" component="p" color="text.primary">
              {header}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {subHeader}
            </Typography>
          </>
        )}
      </Stack>

      {!!actions?.length && !isLoading && (
        <DropdownMenu
          isLoading={isPending}
          options={actions}
          button={{
            horizontal: true,
            disableRipple: true,
            sx: {
              p: important(theme.spacing(1)),
              flexShrink: 0,
              ':hover': {
                bgcolor: 'transparent',
              },
            },
          }}
        />
      )}
    </Box>
  );
}

export default ChatBoxHeader;
